import { createFileRoute, Outlet, Link, Navigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Loader2,
  ShieldAlert,
  Menu,
  X,
  LayoutDashboard,
  ArrowLeftRight,
  CheckCircle2,
  FileText,
  Building2,
  Scale,
  Target,
  History,
  Settings,
  LogOut,
} from "lucide-react";
import { useSessao } from "@/hooks/useAuth";
import { papelLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated")({ component: Shell });

const nav = [
  { to: "/painel", label: "Painel", icon: LayoutDashboard },
  { to: "/lancamentos", label: "Lançamentos", icon: ArrowLeftRight },
  { to: "/aprovacoes", label: "Aprovações", icon: CheckCircle2 },
  { to: "/contratos", label: "Contratos", icon: FileText },
  { to: "/intercompany", label: "Intercompany", icon: Building2 },
  { to: "/conciliacao", label: "Conciliação", icon: Scale },
  { to: "/orcamento", label: "Orçamento", icon: Target },
  { to: "/auditoria", label: "Auditoria", icon: History },
  { to: "/cadastros", label: "Cadastros", icon: Settings },
] as const;

const ITEM = "flex items-center gap-2.5 rounded-full px-3 py-2 text-sm transition-colors";
const ITEM_ATIVO = "bg-surface-2 text-foreground";

function Shell() {
  const { estado, usuario, papeis, erro, sair } = useSessao();
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  const [menuAberto, setMenuAberto] = useState(false);

  // Navegar fecha o menu. Sem isto, no celular a pessoa toca num item e fica
  // olhando a lista aberta por cima da página que acabou de abrir.
  useEffect(() => setMenuAberto(false), [caminho]);

  if (estado === "carregando" || estado === "liberando") {
    return (
      <div className="flex min-h-dvh items-center justify-center text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  if (estado === "deslogado") return <Navigate to="/auth" />;

  if (estado === "negado") {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6">
        <div className="w-full max-w-md">
          <p className="label-caps flex items-center gap-1.5 text-negative">
            <ShieldAlert className="size-3.5" /> Acesso não liberado
          </p>
          <h1 className="font-display mt-3 text-2xl">Sua conta ainda não entra aqui</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {erro ?? "Sua conta ainda não tem papel atribuído no Parker."}
          </p>
          <Button variant="outline" className="mt-6" onClick={() => void sair()}>
            Sair
          </Button>
        </div>
      </main>
    );
  }

  const atual = nav.find((n) => caminho.startsWith(n.to));

  return (
    <div className="flex min-h-dvh bg-background">
      {/* ── coluna fixa, do tablet para cima ── */}
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-hairline md:flex">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <span className="font-display flex size-7 items-center justify-center rounded-full bg-emerald text-sm text-background">
            P
          </span>
          <span className="font-display text-lg">Parker</span>
          <span className="label-caps ml-auto">Lonepine</span>
        </div>
        <hr className="rule-horizon mx-5" />

        <nav className="flex-1 space-y-0.5 px-3 py-4">
          {nav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={cn(ITEM, "text-muted-foreground hover:bg-surface-2 hover:text-foreground")}
              activeProps={{ className: cn(ITEM, ITEM_ATIVO) }}
            >
              <item.icon className="size-4 shrink-0" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="border-t border-hairline px-5 py-4">
          <div className="truncate text-sm">
            {usuario?.displayName ?? usuario?.email ?? "Usuário"}
          </div>
          <div className="label-caps truncate">
            {papeis.map((p) => papelLabel[p] ?? p).join(" · ") || "sem papel"}
          </div>
          <button
            type="button"
            onClick={() => void sair()}
            className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <LogOut className="size-3.5" /> Sair
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          No celular a barra de 9 abas rolando de lado não dizia onde a pessoa
          estava nem o que mais existia. Agora a barra mostra a página atual e um
          botão abre a lista inteira — nove destinos cabem numa lista, não numa
          régua horizontal.
        */}
        <header className="sticky top-0 z-20 border-b border-hairline bg-background md:hidden">
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="font-display flex size-7 shrink-0 items-center justify-center rounded-full bg-emerald text-sm text-background">
              P
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {atual?.label ?? "Parker"}
            </span>
            <Button
              variant="outline"
              size="sm"
              aria-expanded={menuAberto}
              aria-controls="menu-parker"
              onClick={() => setMenuAberto((v) => !v)}
            >
              {menuAberto ? <X className="size-4" /> : <Menu className="size-4" />}
              {menuAberto ? "Fechar" : "Menu"}
            </Button>
          </div>

          {menuAberto ? (
            <nav id="menu-parker" className="border-t border-hairline px-3 py-3">
              {nav.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(ITEM, "text-muted-foreground")}
                  activeProps={{ className: cn(ITEM, ITEM_ATIVO) }}
                >
                  <item.icon className="size-4 shrink-0" />
                  {item.label}
                </Link>
              ))}
              <div className="mt-3 flex items-center justify-between border-t border-hairline px-3 pt-3">
                <div className="min-w-0">
                  <div className="truncate text-sm">
                    {usuario?.displayName ?? usuario?.email ?? "Usuário"}
                  </div>
                  <div className="label-caps truncate">
                    {papeis.map((p) => papelLabel[p] ?? p).join(" · ") || "sem papel"}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => void sair()}>
                  <LogOut className="size-3.5" /> Sair
                </Button>
              </div>
            </nav>
          ) : null}
        </header>

        <main className="min-w-0 flex-1 px-4 py-8 md:px-10 md:py-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
